import { useStore } from 'vuex';
import { JobRoles, UserResponseDto } from '@clearbanc/baus-sdk';
import { computed, Ref } from 'vue';
import { isAuthorizedOwner, JobTitles } from '@clearbanc/data-common-types';

/**
 * Composable for user-related operations and checks, scoped to a specific user ID.
 * @param userIdRef - A Ref or ComputedRef containing the user ID to operate on.
 */
export function useUserManagement(userIdRef: Ref<string | undefined>) {
  const store = useStore();

  // --- Computed Store Getters ---
  const authenticatedUser = computed<UserResponseDto>(() => store.getters.user);

  const businessHasPendingBillContractOrActiveAdvance = computed<boolean>(
    () => store.getters.businessHasPendingBillContractOrActiveAdvance,
  );

  // Current user being acted upon
  const currentUser = computed<UserResponseDto | undefined>(() => {
    if (!userIdRef.value) return undefined;
    const users: UserResponseDto[] = store.getters.allUsers || [];
    return users.find((user) => user.id === userIdRef.value);
  });

  // --- Computed Properties based on userIdRef ---
  const isAuthenticatedUser = computed(() => {
    if (!currentUser.value) return false;
    return currentUser.value.id === authenticatedUser.value.id;
  });

  const canEditUser = computed(() => {
    return !businessHasPendingBillContractOrActiveAdvance.value;
  });

  const isAuthorizedOwnerUser = computed(() => {
    const user = currentUser.value;
    if (!user) return false;
    return isAuthorizedOwner(user.jobTitle as JobTitles, user.jobRoles);
  });

  /**
   * Determines if a user can be deleted based on:
   * 1. The user is not the authenticated user
   * 2. The user is not the primary user
   * 3. The user is not a signatory
   * 4. The user is not an authorized owner and does not have a pending bill contract or active advance
   */
  const canDeleteUser = computed(() => {
    const baseConditions =
      !isAuthenticatedUser.value &&
      !currentUser.value?.isPrimary &&
      !currentUser.value?.jobRoles?.includes(JobRoles.Signatory);

    if (!baseConditions) return false;

    if (isAuthorizedOwnerUser.value) {
      return !businessHasPendingBillContractOrActiveAdvance.value;
    }

    return true;
  });

  const isEligibleToBeSignatory = computed(() => {
    return isAuthorizedOwnerUser.value;
  });

  const isUserProfileCompleted = computed<boolean>(() => {
    const user = currentUser.value;
    if (!user) return false;
    return user.profileCompleted;
  });

  /**
   * Determines if a user can be set as a signatory based on:
   * 1. The profile must be complete
   * 2. The user must be eligible to be a signatory (based on job title/roles)
   * 3. They must not already be a signatory
   * 4. There should be no pending contracts or active advances
   */
  const canSetAsSignatory = computed(() => {
    const user = currentUser.value;
    if (!user) return false;

    // Check if user is already a signatory
    const isSignatory = user.jobRoles?.includes(JobRoles.Signatory);
    if (isSignatory) return false;

    // Check if profile is complete
    if (!isUserProfileCompleted.value) return false;

    // Check if user is eligible to be a signatory
    if (!isEligibleToBeSignatory.value) return false;

    // Check business status
    return !businessHasPendingBillContractOrActiveAdvance.value;
  });

  return {
    isAuthenticatedUser,
    canEditUser,
    canDeleteUser,
    isEligibleToBeSignatory,
    canSetAsSignatory,
    isUserProfileCompleted,
    isAuthorizedOwnerUser,
  };
}
